import { createFileRoute, Link } from "@tanstack/react-router";
import { brl } from "@/lib/format";
import {
  PageHeader,
  Panel,
  PanelHeader,
  Stat,
  GradeFios,
  Th,
  Td,
  EmptyState,
  Erro,
} from "@/components/parker";
import {
  useEmpresas,
  useContas,
  useCentros,
  useContratos,
  useLancamentos,
  useIntercompany,
  useExtrato,
  useRateios,
  usePorLancamento,
  useProjetos,
  nomePorId,
} from "@/lib/dados";
import type { Lancamento } from "@/lib/tipos";
import { agruparPorVencimento, emDias } from "@/lib/vencimentos";
import { partesDe, resultadoPor } from "@/lib/atribuicao";

export const Route = createFileRoute("/_authenticated/painel")({ component: Painel });

/** Receita entra, despesa sai. Não operacional segue o próprio sinal do valor. */
function sinal(l: Lancamento): number {
  if (l.tipo === "receita") return 1;
  if (l.tipo === "despesa") return -1;
  return 1;
}

/** Status em que o dinheiro já se moveu de fato. */
const JA_MOVEU = ["pago", "conciliado"];
/** Status que ainda vão mover o caixa, e por isso entram na projeção. */
const VAI_MOVER = ["aprovado", "aguardando_aprovacao"];
/** Status que não contam no resultado gerencial. */
const FORA_DO_RESULTADO = ["cancelado", "rascunho", "estornado"];

function Painel() {
  const empresas = useEmpresas();
  const contas = useContas();
  const centros = useCentros();
  const contratos = useContratos();
  const lancamentos = useLancamentos();
  const intercompany = useIntercompany();
  const extrato = useExtrato();
  const rateios = useRateios();
  const projetos = useProjetos();
  // Todo hook antes de qualquer return: a ordem das chamadas não pode variar.
  const rateioPorLancamento = usePorLancamento(rateios.dados);

  const erro = lancamentos.erro ?? empresas.erro ?? contas.erro;
  if (erro) return <Erro mensagem={erro} />;

  const lista = lancamentos.dados;

  const saldoInicial = contas.dados.reduce((s, c) => s + Number(c.saldoInicialCentavos ?? 0), 0);
  const caixa =
    saldoInicial +
    lista
      .filter((l) => JA_MOVEU.includes(l.status))
      .reduce((s, l) => s + sinal(l) * Number(l.valorCentavos), 0);

  /** Caixa de hoje mais tudo que tem data de caixa dentro da janela. */
  const horizonte = (dias: number) => {
    const limite = new Date();
    limite.setDate(limite.getDate() + dias);
    const corte = limite.toISOString().slice(0, 10);
    return (
      caixa +
      lista
        .filter((l) => VAI_MOVER.includes(l.status) && l.dataCaixa && l.dataCaixa <= corte)
        .reduce((s, l) => s + sinal(l) * Number(l.valorCentavos), 0)
    );
  };

  const projecao30 = horizonte(30);
  const projecao60 = horizonte(60);

  const pendentes = lista.filter((l) => ["rascunho", "aguardando_aprovacao"].includes(l.status));
  const naoConciliados = extrato.dados.filter((m) => m.situacao === "divergente").length;

  // O resultado é de quem foi beneficiado, não de quem pagou — é o que separa o
  // caixa da holding do desempenho de cada operação. Com rateio, a despesa se
  // parte entre as empresas, e é a parte que conta.
  const partes = partesDe(
    lista.filter((l) => !FORA_DO_RESULTADO.includes(l.status)),
    rateioPorLancamento,
  );
  const resultadoEmpresa = resultadoPor(partes, "empresaId");
  const resultadoCentro = resultadoPor(partes, "centroCustoId");
  const resultadoProjeto = resultadoPor(partes, "projetoId");

  // Projetos com gasto reconhecido. É aqui que a fase pré-operacional de uma
  // empresa promovida continua visível: o histórico não mudou de empresa, mas
  // a conta do projeto mostra quanto a formação custou (§2).
  const porProjeto = projetos.dados
    .map((p) => {
      const r = resultadoProjeto.get(p.id) ?? { receita: 0, despesa: 0, resultado: 0 };
      const virouEmpresa = empresas.dados.find((e) => e.id === p.promovidoParaEmpresaId);
      return {
        id: p.id,
        nome: p.nome,
        ativo: p.ativo,
        virouEmpresa: virouEmpresa?.nome ?? null,
        ...r,
      };
    })
    .filter((p) => p.receita !== 0 || p.despesa !== 0);

  const porEmpresa = empresas.dados.map((e) => {
    const r = resultadoEmpresa.get(e.id) ?? { receita: 0, despesa: 0, resultado: 0 };
    return { id: e.id, nome: e.nome, ...r };
  });

  const consolidado = porEmpresa.reduce(
    (acc, e) => ({
      receita: acc.receita + e.receita,
      despesa: acc.despesa + e.despesa,
      resultado: acc.resultado + e.resultado,
    }),
    { receita: 0, despesa: 0, resultado: 0 },
  );

  // Resultado por centro de custo: a mesma régua da empresa, outro corte.
  const porCentro = centros.dados
    .map((c) => {
      const r = resultadoCentro.get(c.id) ?? { receita: 0, despesa: 0, resultado: 0 };
      return { id: c.id, nome: `${c.codigo} · ${c.nome}`, ...r };
    })
    .filter((c) => c.receita !== 0 || c.despesa !== 0);

  // Contas a pagar e a receber: o que já é compromisso e ainda não virou caixa.
  const porVencimento = agruparPorVencimento(
    lista.filter((l) => VAI_MOVER.includes(l.status) && l.dataCaixa),
  );

  const totalAPagar = porVencimento.reduce((s, f) => s + f.aPagar, 0);
  const totalAReceber = porVencimento.reduce((s, f) => s + f.aReceber, 0);

  // Contratos ativos e os que entram no prazo de aviso prévio.
  const contratosAtivos = contratos.dados.filter((c) => c.status === "aprovado");
  const renovacoesProximas = contratosAtivos.filter((c) => {
    if (!c.fim) return false;
    const d = emDias(c.fim);
    return d > 0 && d <= (c.avisoPrevioDias ?? 30);
  });

  const abertos = intercompany.dados.filter((i) => i.status === "aberto");

  return (
    <>
      <PageHeader
        title="Painel"
        subtitle="Visão gerencial consolidada do grupo. Os saldos intercompany são eliminados no consolidado."
      />

      <GradeFios colunas={4}>
        <Stat
          label="Caixa atual"
          value={brl(caixa)}
          hint="Saldo das contas + movimentos já pagos"
          tone={caixa < 0 ? "negative" : "default"}
        />
        <Stat
          label="Projeção 30 dias"
          value={brl(projecao30)}
          hint="Inclui aprovado e aguardando aprovação"
          tone={projecao30 < 0 ? "negative" : "default"}
        />
        <Stat
          label="Projeção 60 dias"
          value={brl(projecao60)}
          tone={projecao60 < 0 ? "negative" : "default"}
        />
        <Stat
          label="Pendências"
          value={`${pendentes.length} aprov. · ${naoConciliados} concil.`}
          tone={pendentes.length + naoConciliados > 0 ? "warning" : "default"}
        />
      </GradeFios>

      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <Panel className="overflow-hidden lg:col-span-2">
          <PanelHeader
            title="Resultado gerencial por empresa"
            extra={<span className="label-caps">competência reconhecida</span>}
          />
          <table className="w-full text-sm">
            <thead>
              <tr>
                <Th>Empresa</Th>
                <Th className="text-right">Receita</Th>
                <Th className="text-right">Despesa</Th>
                <Th className="text-right">Resultado</Th>
              </tr>
            </thead>
            <tbody>
              {porEmpresa.map((e) => (
                <tr key={e.id}>
                  <Td>{e.nome}</Td>
                  <Td className="num text-right text-positive">{brl(e.receita)}</Td>
                  <Td className="num text-right text-negative">{brl(e.despesa)}</Td>
                  <Td
                    className={`num text-right font-medium ${e.resultado < 0 ? "text-negative" : ""}`}
                  >
                    {brl(e.resultado)}
                  </Td>
                </tr>
              ))}
              <tr className="bg-surface-2">
                <Td className="font-semibold">Consolidado do grupo</Td>
                <Td className="num text-right">{brl(consolidado.receita)}</Td>
                <Td className="num text-right">{brl(consolidado.despesa)}</Td>
                <Td className="num text-right font-semibold">{brl(consolidado.resultado)}</Td>
              </tr>
            </tbody>
          </table>
          {porEmpresa.length === 0 && !empresas.carregando ? (
            <EmptyState
              title="Nenhuma empresa cadastrada"
              hint="Comece pelos Cadastros para registrar as empresas do grupo."
            />
          ) : null}
        </Panel>

        <Panel className="overflow-hidden">
          <PanelHeader title="Posição intercompany" />
          {abertos.length === 0 ? (
            <EmptyState
              title="Nenhum saldo em aberto"
              hint="As obrigações nascem sozinhas quando uma empresa paga por outra."
            />
          ) : (
            <ul className="divide-y divide-hairline">
              {abertos.slice(0, 8).map((i) => (
                <li
                  key={i.id}
                  className="flex items-center justify-between gap-3 px-4 py-3 text-sm"
                >
                  <span className="min-w-0 truncate text-muted-foreground">
                    {nomePorId(empresas.dados, i.empresaDevedoraId)} →{" "}
                    {nomePorId(empresas.dados, i.empresaCredoraId)}
                  </span>
                  <span className="num shrink-0 font-medium">{brl(i.valorCentavos)}</span>
                </li>
              ))}
            </ul>
          )}
          <div className="border-t border-hairline px-4 py-3">
            <Link to="/intercompany" className="text-xs text-emerald hover:underline">
              Ver posição completa
            </Link>
          </div>
        </Panel>
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <Panel className="overflow-hidden">
          <PanelHeader
            title="Contas a pagar e a receber"
            extra={<span className="label-caps">por vencimento</span>}
          />
          {porVencimento.length === 0 ? (
            <EmptyState
              title="Nada em aberto"
              hint="Aparece aqui o que já é compromisso e ainda não virou caixa."
            />
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <Th>Faixa</Th>
                  <Th className="text-right">A pagar</Th>
                  <Th className="text-right">A receber</Th>
                </tr>
              </thead>
              <tbody>
                {porVencimento.map((f) => (
                  <tr key={f.rotulo}>
                    <Td className={f.vencida ? "font-medium text-negative" : undefined}>
                      {f.rotulo}
                      <span className="label-caps ml-2">{f.n}</span>
                    </Td>
                    <Td className="num text-right">{f.aPagar ? brl(f.aPagar) : "—"}</Td>
                    <Td className="num text-right text-positive">
                      {f.aReceber ? brl(f.aReceber) : "—"}
                    </Td>
                  </tr>
                ))}
                <tr className="bg-surface-2">
                  <Td className="font-semibold">Total em aberto</Td>
                  <Td className="num text-right font-semibold">{brl(totalAPagar)}</Td>
                  <Td className="num text-right font-semibold">{brl(totalAReceber)}</Td>
                </tr>
              </tbody>
            </table>
          )}
        </Panel>

        <Panel className="overflow-hidden">
          <PanelHeader
            title="Resultado por centro de custo"
            extra={<span className="label-caps">competência reconhecida</span>}
          />
          {porCentro.length === 0 ? (
            <EmptyState
              title="Nada reconhecido ainda"
              hint="Os centros aparecem assim que houver lançamento neles."
            />
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <Th>Centro de custo</Th>
                  <Th className="text-right">Receita</Th>
                  <Th className="text-right">Despesa</Th>
                  <Th className="text-right">Resultado</Th>
                </tr>
              </thead>
              <tbody>
                {porCentro.map((c) => (
                  <tr key={c.id}>
                    <Td>{c.nome}</Td>
                    <Td className="num text-right text-positive">
                      {c.receita ? brl(c.receita) : "—"}
                    </Td>
                    <Td className="num text-right text-negative">
                      {c.despesa ? brl(c.despesa) : "—"}
                    </Td>
                    <Td
                      className={`num text-right font-medium ${c.resultado < 0 ? "text-negative" : ""}`}
                    >
                      {brl(c.resultado)}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>
      </div>

      {porProjeto.length > 0 ? (
        <Panel className="mt-4 overflow-hidden">
          <PanelHeader
            title="Resultado por projeto"
            extra={<span className="label-caps">inclui fase pré-operacional</span>}
          />
          <table className="w-full text-sm">
            <thead>
              <tr>
                <Th>Projeto</Th>
                <Th className="text-right">Receita</Th>
                <Th className="text-right">Despesa</Th>
                <Th className="text-right">Resultado</Th>
              </tr>
            </thead>
            <tbody>
              {porProjeto.map((p) => (
                <tr key={p.id}>
                  <Td>
                    {p.nome}
                    {p.virouEmpresa ? (
                      <div className="label-caps text-emerald">
                        fase pré-operacional de {p.virouEmpresa}
                      </div>
                    ) : !p.ativo ? (
                      <div className="label-caps">encerrado</div>
                    ) : null}
                  </Td>
                  <Td className="num text-right text-positive">
                    {p.receita ? brl(p.receita) : "—"}
                  </Td>
                  <Td className="num text-right text-negative">
                    {p.despesa ? brl(p.despesa) : "—"}
                  </Td>
                  <Td
                    className={`num text-right font-medium ${p.resultado < 0 ? "text-negative" : ""}`}
                  >
                    {brl(p.resultado)}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      ) : null}

      <Panel className="mt-4 overflow-hidden">
        <PanelHeader
          title="Contratos"
          extra={
            <Link to="/contratos" className="text-xs text-emerald hover:underline">
              Ver contratos
            </Link>
          }
        />
        {contratosAtivos.length === 0 ? (
          <EmptyState
            title="Nenhum contrato ativo"
            hint="Contratos aprovados aparecem aqui, com os vencimentos próximos em destaque."
          />
        ) : (
          <>
            <div className="flex flex-wrap gap-6 px-4 py-3 text-sm">
              <div>
                <span className="label-caps">Ativos</span>
                <div className="num mt-1 font-semibold">{contratosAtivos.length}</div>
              </div>
              <div>
                <span className="label-caps">Renovações próximas</span>
                <div
                  className={`num mt-1 font-semibold ${renovacoesProximas.length ? "text-warning" : ""}`}
                >
                  {renovacoesProximas.length}
                </div>
              </div>
              <div>
                <span className="label-caps">Compromisso por parcela</span>
                <div className="num mt-1 font-semibold">
                  {brl(contratosAtivos.reduce((s, c) => s + Number(c.valorCentavos), 0))}
                </div>
              </div>
            </div>
            {renovacoesProximas.length > 0 ? (
              <ul className="divide-y divide-hairline border-t border-hairline">
                {renovacoesProximas.map((c) => (
                  <li key={c.id} className="flex justify-between gap-3 px-4 py-2.5 text-sm">
                    <span className="min-w-0 truncate">{c.descricao}</span>
                    <span className="label-caps shrink-0 text-warning">
                      vence em {emDias(c.fim!)} dias
                    </span>
                  </li>
                ))}
              </ul>
            ) : null}
          </>
        )}
      </Panel>

      <Panel className="mt-4 overflow-hidden">
        <PanelHeader
          title="Aguardando tratamento"
          extra={
            <Link to="/aprovacoes" className="text-xs text-emerald hover:underline">
              Ir para aprovações
            </Link>
          }
        />
        {pendentes.length === 0 ? (
          <EmptyState title="Nada pendente" hint="Todos os lançamentos estão tratados." />
        ) : (
          <ul className="divide-y divide-hairline">
            {pendentes.slice(0, 6).map((l) => (
              <li key={l.id} className="flex items-center justify-between gap-3 px-4 py-3 text-sm">
                <span className="min-w-0 truncate">{l.descricao}</span>
                <span className="num shrink-0">{brl(l.valorCentavos)}</span>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </>
  );
}
